// The Run page takes its config as pasted JSON and rebuilds the job payload from named sections.
// Any key it does not know is not forwarded, so a key that is silently ignored changes what the
// job does without telling the user. This reads the sections out and names the rest.
import type { ApiBacktestConfig } from "@tradingdroid/frontends-types";
import {
  API_CROSS_DIRECTION_REVERSAL_POLICIES,
  API_SAME_CANDLE_REENTRY_MODES,
  API_SAME_CANDLE_REENTRY_UNRESOLVED,
} from "@tradingdroid/frontends-types";

// The keys the worker refuses in a live job, whatever their value.
export const BACKTEST_ONLY_ENTRY_POLICY_KEYS = [
  "same_candle_reentry",
  "same_candle_reentry_unresolved",
  "cross_direction_reversal",
  "fill_at_open",
] as const;

type BacktestOnlyEntryPolicyKey = (typeof BACKTEST_ONLY_ENTRY_POLICY_KEYS)[number];

const BACKTEST_ONLY_ENTRY_POLICY_VALUES: Record<BacktestOnlyEntryPolicyKey, readonly unknown[]> = {
  same_candle_reentry: API_SAME_CANDLE_REENTRY_MODES,
  same_candle_reentry_unresolved: API_SAME_CANDLE_REENTRY_UNRESOLVED,
  cross_direction_reversal: API_CROSS_DIRECTION_REVERSAL_POLICIES,
  fill_at_open: [true, false],
};

// The run form's legacy top-level cost toggles. The Run page reads them into the job's cost map.
export const LEGACY_COST_TOGGLE_KEYS = ["apply_slippage", "apply_fees"] as const;

export const RUN_CONFIG_KEYS = [
  "strategy_config",
  "exit_config",
  "regime_config",
  "cost_config",
  ...LEGACY_COST_TOGGLE_KEYS,
  "base_step_size",
  "base_max_amount",
  "base_min_amount",
  ...BACKTEST_ONLY_ENTRY_POLICY_KEYS,
] as const;

export interface RunConfigSections extends Pick<ApiBacktestConfig, BacktestOnlyEntryPolicyKey> {
  strategy_config?: Record<string, unknown>;
  exit_config?: Record<string, unknown>;
  regime_config?: Record<string, unknown>;
  cost_config?: Record<string, unknown>;
  apply_slippage?: boolean;
  apply_fees?: boolean;
  base_step_size?: number | null;
  base_max_amount?: number | null;
  base_min_amount?: number | null;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Returns the message that blocks the submit, or null when the config can be forwarded as is. */
export const validateRunConfigShape = (
  parsed: Record<string, unknown>,
  runType: "backtest" | "execution",
): string | null => {
  const unknown = Object.keys(parsed).filter(
    (key) => !(RUN_CONFIG_KEYS as readonly string[]).includes(key),
  );
  if (unknown.length > 0) {
    return `Config has keys the run form does not send: ${unknown.join(", ")}`;
  }
  if ("regime_config" in parsed && !isPlainObject(parsed.regime_config)) {
    return "regime_config must be a JSON object";
  }
  if (runType === "execution") {
    const present = BACKTEST_ONLY_ENTRY_POLICY_KEYS.filter((key) => key in parsed);
    if (present.length === 1) {
      return `${present[0]} is a backtest-only setting and a live run refuses it; remove it from the config`;
    }
    if (present.length > 1) {
      return `${present.join(" and ")} are backtest-only settings and a live run refuses them; remove them from the config`;
    }
  }
  for (const key of BACKTEST_ONLY_ENTRY_POLICY_KEYS) {
    if (key in parsed && !BACKTEST_ONLY_ENTRY_POLICY_VALUES[key].includes(parsed[key])) {
      return `${key} must be one of: ${BACKTEST_ONLY_ENTRY_POLICY_VALUES[key].join(", ")}`;
    }
  }
  return null;
};

export const splitRunConfig = (parsed: Record<string, unknown>): RunConfigSections => {
  const sections: RunConfigSections = {};
  for (const key of RUN_CONFIG_KEYS) {
    if (key in parsed) {
      (sections as Record<string, unknown>)[key] = parsed[key];
    }
  }
  return sections;
};
